/**
 * The one festival page's form, pure (no React): what a page seeds, what each edit does, and how the curator's unsaved
 * edits survive a reload.
 *
 * One reducer holds every edit of the page (the name, the days with their venues and lineups, the tray, the choices
 * for events already in Mood, the pages Tina read). The bodies Approve and Publish changes send are `page-request.ts`'s;
 * what Tina will make is Tina's own dry run, not computed here. Days are `YYYY-MM-DD` strings throughout, and no date
 * goes through the local time zone.
 *
 * A day is known by `loadedDay`, the date it was stored under: it stays put when the curator moves the day, so the
 * server can tell a moved day from a removed one plus a new one. A day the curator added has none.
 */
import { isEqual } from "lodash";

import { dayRange, parseDay } from "@/lib/tina/festivals";
import type {
  ExistingChoiceKind,
  FestivalPageRead,
  LineupEntryRead,
} from "@/types/tina-festival-page";

export interface FormEntry {
  /** Names a row for React and for a drag; never sent. */
  key: string;
  name: string;
  artistId: string | null;
  setTime: string;
  stage: string;
  /** The picked artist's photo; null shows initials. */
  photoUrl: string | null;
}

export interface FormDay {
  key: string;
  /** The date the day is stored under; null for a day the curator added. */
  loadedDay: string | null;
  day: string;
  venueId: string | null;
  venueName: string;
  lineup: FormEntry[];
  cancelled: boolean;
}

export interface ExistingChoiceForm {
  choice: ExistingChoiceKind;
  day: string | null;
}

/** A stored day the curator took off an approved page. `box` is what it held, so it can be put back. */
export interface RemovedDay {
  deleteInMood: boolean;
  box: FormDay;
}

export interface PageForm {
  /** The festival has been approved: the page edits an approved festival and its body is Publish changes. */
  afterApproval: boolean;
  name: string;
  aliases: string[];
  days: FormDay[];
  /** Stored days taken off the page, keyed by the stored date. */
  removed: Record<string, RemovedDay>;
  /** Names the page lists but has not placed on a day. */
  tray: string[];
  /** Tray names the curator dropped from an approved page (sent, so Tina does not offer them again). */
  trayDropped: string[];
  /** The choice for each Mood event already in the festival's place, by Mood event id. */
  existing: Record<string, ExistingChoiceForm>;
  /** Each page Tina read, by url. */
  pages: Record<string, { member: boolean; ownPage: boolean }>;
  posterUrl: string;
  /** Counts the keys made so far, so a fresh key is never one already used. */
  seq: number;
}

export type PageFormAction =
  | { type: "set_name"; name: string }
  | { type: "set_aliases"; aliases: string[] }
  | { type: "add_day" }
  | { type: "remove_day"; key: string }
  | { type: "restore_day"; loadedDay: string }
  | { type: "set_delete_in_mood"; loadedDay: string; on: boolean }
  | { type: "set_date"; key: string; day: string }
  | {
      type: "set_venue";
      key: string;
      venue: { id: string; name: string } | null;
    }
  | { type: "set_lineup"; key: string; lineup: FormEntry[] }
  | { type: "add_artist"; key: string }
  | { type: "place"; name: string; key: string }
  | { type: "place_all" }
  | { type: "drop_tray"; name: string }
  | { type: "set_cancelled"; key: string; on: boolean }
  | {
      type: "set_choice";
      moodEventId: string;
      choice: ExistingChoiceKind;
      day: string | null;
    }
  | { type: "set_member"; url: string; member: boolean }
  | { type: "set_own_page"; url: string | null }
  | { type: "set_poster"; url: string };

/** Trimmed, lower case, accents stripped: how two spellings of one act's name are told to be one. */
export function foldName(name: string): string {
  return name.trim().toLowerCase().normalize("NFD").replace(/\p{M}/gu, "");
}

/** Each day's number, counted from 1 by date: two boxes on one date share a number, as Tina's `day_number` does. */
export function dayNumbers(days: FormDay[]): Map<string, number> {
  const dates = [...new Set(days.map((d) => d.day))].sort();
  return new Map(days.map((d) => [d.key, dates.indexOf(d.day) + 1]));
}

// ── Seeding ─────────────────────────────────────────────────────────────────

function entryFrom(read: LineupEntryRead, key: string): FormEntry {
  return {
    key,
    name: read.name,
    artistId: read.artist_id,
    setTime: read.set_time ?? "",
    stage: read.stage ?? "",
    photoUrl: read.photo_url,
  };
}

/** The form a page seeds: every field as Tina read it, no edit made. */
export function initialPageForm(page: FestivalPageRead): PageForm {
  const seen = new Map<string, number>();
  const days = page.days.map((box): FormDay => {
    const count = (seen.get(box.day) ?? 0) + 1;
    seen.set(box.day, count);
    const key = count === 1 ? `day-${box.day}` : `day-${box.day}-${count}`;
    return {
      key,
      loadedDay: box.day,
      day: box.day,
      venueId: box.venue_id,
      venueName: box.venue_name ?? "",
      lineup: box.lineup.map((read, i) => entryFrom(read, `${key}/${i}`)),
      cancelled: box.cancelled,
    };
  });
  return {
    afterApproval: page.page_version !== null,
    name: page.name,
    aliases: page.other_names.filter((n) => n.trim() !== page.name.trim()),
    days,
    removed: {},
    tray: [...page.tray],
    trayDropped: [],
    existing: Object.fromEntries(
      page.existing.map((line) => [
        line.mood_event_id,
        {
          choice: line.choice,
          // An event Tina found that has no target day folds into its own day.
          day:
            line.target_day ??
            (line.choice === "replace" ? line.service_day : null),
        },
      ]),
    ),
    pages: Object.fromEntries(
      page.pages.map((p) => [p.url, { member: p.member, ownPage: p.own_page }]),
    ),
    posterUrl: "",
    seq: 0,
  };
}

// ── Comparing ───────────────────────────────────────────────────────────────

const plainEntry = ({ key: _key, ...entry }: FormEntry) => entry;
const plainDay = ({ key: _key, ...day }: FormDay) => ({
  ...day,
  lineup: day.lineup.map(plainEntry),
});

/** The form without what is only bookkeeping (keys and the key counter), for comparing two forms. */
function plainForm(form: PageForm) {
  return {
    ...form,
    seq: 0,
    days: form.days.map(plainDay),
    removed: Object.fromEntries(
      Object.entries(form.removed).map(([day, r]) => [
        day,
        { deleteInMood: r.deleteInMood, box: plainDay(r.box) },
      ]),
    ),
  };
}

/** Whether the form differs from `base`; an edit undone is not a difference. */
export function isDirty(form: PageForm, base: PageForm): boolean {
  return !isEqual(plainForm(form), plainForm(base));
}

// ── Editing ─────────────────────────────────────────────────────────────────

const firstDay = (days: FormDay[]): string | null =>
  days.reduce<string | null>(
    (first, d) => (first === null || d.day < first ? d.day : first),
    null,
  );

/** The day after `day`, read from its parts so no time zone moves it. */
function nextDay(day: string): string {
  const parts = parseDay(day);
  if (parts === null) return day;
  return new Date(Date.UTC(parts.year, parts.month - 1, parts.day + 1))
    .toISOString()
    .slice(0, 10);
}

/**
 * Every choice that names a day no box has: "Use as Day" becomes "Replace" on the first day left, a "Replace" moves to
 * the first day left. With no day left a choice names none.
 */
function repairChoices(
  existing: PageForm["existing"],
  days: FormDay[],
): PageForm["existing"] {
  const dates = new Set(days.map((d) => d.day));
  const first = firstDay(days);
  return Object.fromEntries(
    Object.entries(existing).map(([id, line]) => {
      if (
        line.day === null ||
        line.choice === "not_part" ||
        dates.has(line.day)
      ) {
        return [id, line];
      }
      return [id, { choice: "replace" as const, day: first }];
    }),
  );
}

/** Choices that named `from` now name `to` (a day moved). */
function moveChoices(
  existing: PageForm["existing"],
  from: string,
  to: string,
): PageForm["existing"] {
  return Object.fromEntries(
    Object.entries(existing).map(([id, line]) => [
      id,
      line.day === from ? { ...line, day: to } : line,
    ]),
  );
}

const mapDay = (
  form: PageForm,
  key: string,
  change: (day: FormDay) => FormDay,
): PageForm => ({
  ...form,
  days: form.days.map((d) => (d.key === key ? change(d) : d)),
});

/** Puts `name` on `day` unless it holds it already (folded); `form` supplies the fresh key. */
function withArtist(day: FormDay, name: string, key: string): FormDay {
  const folded = foldName(name);
  if (day.lineup.some((e) => foldName(e.name) === folded)) return day;
  return {
    ...day,
    lineup: [
      ...day.lineup,
      { key, name, artistId: null, setTime: "", stage: "", photoUrl: null },
    ],
  };
}

function insertByDate(days: FormDay[], box: FormDay): FormDay[] {
  const at = days.findIndex((d) => d.day > box.day);
  return at === -1
    ? [...days, box]
    : [...days.slice(0, at), box, ...days.slice(at)];
}

export function pageFormReducer(
  form: PageForm,
  action: PageFormAction,
): PageForm {
  switch (action.type) {
    case "set_name":
      return { ...form, name: action.name };
    case "set_aliases":
      return { ...form, aliases: action.aliases };
    case "add_day": {
      const last = form.days.reduce<FormDay | null>(
        (latest, d) => (latest === null || d.day >= latest.day ? d : latest),
        null,
      );
      if (last === null) return form;
      const box: FormDay = {
        key: `n${form.seq}`,
        loadedDay: null,
        day: nextDay(last.day),
        venueId: last.venueId,
        venueName: last.venueName,
        lineup: [],
        cancelled: false,
      };
      return { ...form, days: [...form.days, box], seq: form.seq + 1 };
    }
    case "remove_day": {
      const gone = form.days.find((d) => d.key === action.key);
      if (!gone) return form;
      const days = form.days.filter((d) => d.key !== action.key);
      const removed =
        gone.loadedDay !== null && form.afterApproval
          ? {
              ...form.removed,
              [gone.loadedDay]: { deleteInMood: false, box: gone },
            }
          : form.removed;
      // Another box on the same date keeps the choices that name it.
      const existing = days.some((d) => d.day === gone.day)
        ? form.existing
        : repairChoices(form.existing, days);
      return { ...form, days, removed, existing };
    }
    case "restore_day": {
      const back = form.removed[action.loadedDay];
      if (!back) return form;
      const { [action.loadedDay]: _restored, ...removed } = form.removed;
      return { ...form, days: insertByDate(form.days, back.box), removed };
    }
    case "set_delete_in_mood": {
      const entry = form.removed[action.loadedDay];
      if (!entry) return form;
      return {
        ...form,
        removed: {
          ...form.removed,
          [action.loadedDay]: { ...entry, deleteInMood: action.on },
        },
      };
    }
    case "set_date": {
      const moved = form.days.find((d) => d.key === action.key);
      if (!moved || moved.day === action.day) return form;
      const next = mapDay(form, action.key, (d) => ({ ...d, day: action.day }));
      const keepsOld = next.days.some((d) => d.day === moved.day);
      return keepsOld
        ? next
        : {
            ...next,
            existing: moveChoices(form.existing, moved.day, action.day),
          };
    }
    case "set_venue":
      return mapDay(form, action.key, (d) => ({
        ...d,
        venueId: action.venue?.id ?? null,
        venueName: action.venue?.name ?? "",
      }));
    case "set_lineup":
      return mapDay(form, action.key, (d) => ({ ...d, lineup: action.lineup }));
    case "add_artist":
      return {
        ...mapDay(form, action.key, (d) => ({
          ...d,
          lineup: [
            ...d.lineup,
            {
              key: `n${form.seq}`,
              name: "",
              artistId: null,
              setTime: "",
              stage: "",
              photoUrl: null,
            },
          ],
        })),
        seq: form.seq + 1,
      };
    case "place": {
      const placed = mapDay(form, action.key, (d) =>
        withArtist(d, action.name, `n${form.seq}`),
      );
      return {
        ...placed,
        tray: form.tray.filter((n) => n !== action.name),
        seq: form.seq + 1,
      };
    }
    case "place_all": {
      let seq = form.seq;
      const days = form.days.map((d) =>
        form.tray.reduce((acc, name) => withArtist(acc, name, `n${seq++}`), d),
      );
      return { ...form, days, tray: [], seq };
    }
    case "drop_tray":
      return {
        ...form,
        tray: form.tray.filter((n) => n !== action.name),
        trayDropped:
          form.afterApproval && !form.trayDropped.includes(action.name)
            ? [...form.trayDropped, action.name]
            : form.trayDropped,
      };
    case "set_cancelled":
      return mapDay(form, action.key, (d) => ({ ...d, cancelled: action.on }));
    case "set_choice":
      return {
        ...form,
        existing: {
          ...form.existing,
          [action.moodEventId]: { choice: action.choice, day: action.day },
        },
      };
    case "set_member": {
      const page = form.pages[action.url];
      if (!page) return form;
      return {
        ...form,
        pages: {
          ...form.pages,
          [action.url]: { ...page, member: action.member },
        },
      };
    }
    case "set_own_page":
      return {
        ...form,
        pages: Object.fromEntries(
          Object.entries(form.pages).map(([url, page]) => [
            url,
            { ...page, ownPage: url === action.url },
          ]),
        ),
      };
    case "set_poster":
      return { ...form, posterUrl: action.url };
  }
}

// ── Rebasing ────────────────────────────────────────────────────────────────

export interface RebaseResult {
  form: PageForm;
  /** One plain sentence per edit that did not survive, for the page's notice. */
  dropped: string[];
}

const identity = (entry: FormEntry): string =>
  entry.artistId ?? `name:${foldName(entry.name)}`;

const sameEntries = (a: FormEntry[], b: FormEntry[]): boolean =>
  isEqual(a.map(plainEntry), b.map(plainEntry));

/**
 * One day's lineup after a reload. Untouched on one side: the other side's. Edited on both: mine, minus what theirs
 * removed that mine left as it was, plus what theirs added that neither base nor mine has, in theirs' order.
 */
function mergeLineup(
  base: FormEntry[],
  mine: FormEntry[],
  theirs: FormEntry[],
  freshKey: () => string,
): FormEntry[] {
  if (sameEntries(mine, base)) return theirs;
  if (sameEntries(theirs, base)) return mine;
  const theirIds = new Set(theirs.map(identity));
  const baseIds = new Set(base.map(identity));
  const mineIds = new Set(mine.map(identity));
  const untouched = (entry: FormEntry): boolean => {
    const was = base.find((b) => identity(b) === identity(entry));
    return was !== undefined && isEqual(plainEntry(was), plainEntry(entry));
  };
  const kept = mine.filter(
    (entry) =>
      !(
        baseIds.has(identity(entry)) &&
        !theirIds.has(identity(entry)) &&
        untouched(entry)
      ),
  );
  const added = theirs
    .filter(
      (entry) => !baseIds.has(identity(entry)) && !mineIds.has(identity(entry)),
    )
    .map((entry) => ({ ...entry, key: freshKey() }));
  return [...kept, ...added];
}

const pick = <T>(base: T, mine: T, theirs: T): T =>
  isEqual(mine, base) ? theirs : mine;

/**
 * The form after the page reloaded under unsaved edits. `base` is the form seeded from the page the curator started
 * from, `mine` is the form now, `theirs` is the form seeded from the fresh page. Each field keeps the curator's edit
 * where there is one and the fresh page's value where there is not; what cannot be kept is listed in `dropped`.
 * `nameOf` names a Mood event for those sentences.
 */
export function rebaseForm(
  base: PageForm,
  mine: PageForm,
  theirs: PageForm,
  nameOf: (moodEventId: string) => string = (id) => id,
): RebaseResult {
  const dropped: string[] = [];
  let seq = Math.max(mine.seq, theirs.seq);
  const freshKey = (): string => `n${seq++}`;

  const loaded = (days: FormDay[]): Map<string, FormDay> =>
    new Map(
      days.flatMap((d) =>
        d.loadedDay === null ? [] : [[d.loadedDay, d] as const],
      ),
    );
  const baseDays = loaded(base.days);
  const mineDays = loaded(mine.days);
  const theirDays = loaded(theirs.days);

  const days: FormDay[] = [];
  for (const m of mine.days) {
    if (m.loadedDay === null) {
      days.push(m);
      continue;
    }
    const t = theirDays.get(m.loadedDay);
    const b = baseDays.get(m.loadedDay);
    if (!t) {
      if (b && !isEqual(plainDay(b), plainDay(m))) {
        dropped.push(
          `${dayRange(m.loadedDay, m.loadedDay)} is no longer on the page, so your edits to it were dropped.`,
        );
      }
      continue;
    }
    const was = b ?? t;
    const venueChanged =
      m.venueId !== was.venueId || m.venueName !== was.venueName;
    days.push({
      key: m.key,
      loadedDay: m.loadedDay,
      day: pick(was.day, m.day, t.day),
      venueId: venueChanged ? m.venueId : t.venueId,
      venueName: venueChanged ? m.venueName : t.venueName,
      cancelled: pick(was.cancelled, m.cancelled, t.cancelled),
      lineup: mergeLineup(was.lineup, m.lineup, t.lineup, freshKey),
    });
  }

  const removed: PageForm["removed"] = {};
  for (const t of theirs.days) {
    if (t.loadedDay === null || mineDays.has(t.loadedDay)) continue;
    // Not on the form I have: theirs added it, or I removed it and it stays removed.
    if (!baseDays.has(t.loadedDay)) {
      days.push(t);
    } else if (theirs.afterApproval) {
      removed[t.loadedDay] = mine.removed[t.loadedDay] ?? {
        deleteInMood: false,
        box: t,
      };
    }
  }

  // Choices: mine where I changed one, theirs otherwise; one for an event theirs no longer lists is dropped.
  const existing: PageForm["existing"] = {};
  for (const [id, line] of Object.entries(theirs.existing)) {
    existing[id] = pick(base.existing[id], mine.existing[id], line) ?? line;
  }
  for (const [id, line] of Object.entries(mine.existing)) {
    if (id in theirs.existing) continue;
    if (!isEqual(line, base.existing[id])) {
      dropped.push(
        `${nameOf(id)} is no longer under Already in Mood, so your choice for it was dropped.`,
      );
    }
  }

  const pages: PageForm["pages"] = {};
  for (const [url, page] of Object.entries(theirs.pages)) {
    pages[url] = pick(base.pages[url], mine.pages[url], page) ?? page;
  }

  const trayDropped = [
    ...new Set([...mine.trayDropped, ...theirs.trayDropped]),
  ];
  const placed = new Set(
    days.flatMap((d) => d.lineup.map((e) => foldName(e.name))),
  );
  const hidden = new Set(trayDropped.map(foldName));
  const tray = theirs.tray.filter(
    (n) => !placed.has(foldName(n)) && !hidden.has(foldName(n)),
  );

  return {
    form: {
      afterApproval: theirs.afterApproval,
      name: pick(base.name, mine.name, theirs.name),
      aliases: pick(base.aliases, mine.aliases, theirs.aliases),
      days,
      removed,
      tray,
      trayDropped,
      existing: repairChoices(existing, days),
      pages,
      posterUrl: pick(base.posterUrl, mine.posterUrl, theirs.posterUrl),
      seq,
    },
    dropped,
  };
}
